import { Skeleton, TableCell, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import React, { useEffect, useRef } from "react";
import { useSlaActivities, useSlaActivityForTask } from "./slaActivitiesContext";

/**
 * **Colonna SLA della lista viva** (Operazioni > In tempo reale): la scadenza di
 * ogni riga, solo con lo SLA dell'organizzazione attivo.
 *
 * Regole:
 * - stato e tempo restante non hanno piu' colonne proprie: sono il chip
 *   `SlaTaskSlaBadge` compatto (lo stesso delle card di Le mie attivita') nella
 *   cella del processo (`TaskItem`); la tabella non ha selettore di colonne ne'
 *   ordinamento per SLA, quindi le due colonne sono state tolte, non nascoste;
 * - i dati vengono dal `SlaActivitiesProvider` che avvolge la tabella (una sola
 *   lettura di `GET /sla/organizations/{org}/instances`, nessuna chiamata per
 *   riga); l'unione con le righe e' nel browser;
 * - per riga vale l'istanza **di attivita'** sul nodo del token
 *   (`designerid`), altrimenti quella **di processo** (`slaActivityForTask`);
 *   senza istanza la cella mostra "—";
 * - la scadenza e' la vigente nella timezone dell'istanza, come nel Monitor;
 * - la colonna si nasconde sotto `md` (tabella gia' larga).
 */

/** Scadenza: nascosta sugli schermi stretti. */
const DEADLINE_CELL_SX = { display: { xs: "none", md: "table-cell" } } as const;
const CELL_SX = { whiteSpace: "nowrap" } as const;

interface SlaLiveRowCellsProps {
  idprocess: number;
  idprocesstoken: number;
  designerid?: string | null;
}

export const SlaLiveHeaderCells: React.FC = () => {
  const translations = useTranslations();
  return (
    <TableCell sx={{ width: "10%", ...CELL_SX, ...DEADLINE_CELL_SX }}>
      {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.columnDeadline)}
    </TableCell>
  );
};

export const SlaLiveSkeletonCells: React.FC = () => (
  <TableCell sx={DEADLINE_CELL_SX}>
    <Skeleton variant="text" width={96} height={16} />
  </TableCell>
);

const EMPTY = "—";

export const SlaLiveRowCells: React.FC<SlaLiveRowCellsProps> = ({
  idprocess,
  idprocesstoken,
  designerid,
}) => {
  const translations = useTranslations();
  const view = useSlaActivityForTask({ idprocess, designerid: designerid || null });
  const testId = `monitor-sla-${idprocesstoken}-deadline`;

  if (!view) {
    return (
      <TableCell data-testid={testId} sx={DEADLINE_CELL_SX}>
        {EMPTY}
      </TableCell>
    );
  }

  const { detail } = view;
  return (
    <TableCell
      data-testid={testId}
      data-timezone={detail.timezoneId}
      sx={{ ...CELL_SX, ...DEADLINE_CELL_SX }}
    >
      <Typography variant="body2">{detail.effectiveLabel || EMPTY}</Typography>
      {detail.overridden && (
        <Typography variant="caption" component="div" color="text.secondary">
          {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}
        </Typography>
      )}
    </TableCell>
  );
};

/**
 * Rilegge le istanze quando la lista viva si rilegge (`refreshKey`). Il primo
 * valore e' coperto dalla lettura al mount del provider.
 */
export const SlaLiveRefreshOnChange: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const context = useSlaActivities();
  const refreshRef = useRef(context?.refresh);
  refreshRef.current = context?.refresh;
  const firstRef = useRef(true);
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    refreshRef.current?.();
  }, [refreshKey]);
  return null;
};
